import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  ArrowLeft, Mail, Shield, Key, ExternalLink, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle2, Copy, HelpCircle, Search
} from 'lucide-react';
import { toast } from 'sonner';

// Provider icons
const GmailIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
  </svg>
);

const OutlookIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576a.806.806 0 0 1-.595.239h-8.167v-6.348l1.44 1.035c.108.086.233.128.375.128s.267-.043.375-.128l5.972-4.318v-.375c0-.23.077-.424.23-.576a.788.788 0 0 1 .583-.239c.23 0 .424.08.576.239.152.152.239.345.239.576v-.287zM23.167 5.333H15v5.648l1.44-1.035a.56.56 0 0 1 .375-.128c.142 0 .267.043.375.128l5.977 4.318V5.858c0-.145-.05-.267-.152-.367a.497.497 0 0 0-.367-.158h-.481zM0 3.5v17c0 .273.1.508.3.708.2.2.433.3.7.3h13c.267 0 .5-.1.7-.3.2-.2.3-.435.3-.708v-17c0-.273-.1-.508-.3-.708-.2-.2-.433-.3-.7-.3H1c-.267 0-.5.1-.7.3-.2.2-.3.435-.3.708zm7 12.167c-1.378 0-2.5-1.455-2.5-3.25s1.122-3.25 2.5-3.25 2.5 1.455 2.5 3.25-1.122 3.25-2.5 3.25z"/>
  </svg>
);

const ICloudIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path fill="#3693F3" d="M13.762 4.29a6.51 6.51 0 0 0-5.669 3.332 3.571 3.571 0 0 0-1.558-.36 3.571 3.571 0 0 0-3.516 3A4.918 4.918 0 0 0 0 14.796a4.918 4.918 0 0 0 4.918 4.914h13.164A5.918 5.918 0 0 0 24 13.792a5.918 5.918 0 0 0-5.918-5.918c-.083 0-.165.003-.247.008a6.51 6.51 0 0 0-4.073-3.592z"/>
  </svg>
);

const YahooIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path fill="#6001D2" d="M12.996 21.336L12 24l-.996-2.664L8.76 24l.672-2.664L7.704 24l1.008-2.664h6.576L16.296 24l-1.728-2.664L15.24 24l-2.244-2.664zM23.28 0L15.6 8.016 12 .672 8.4 8.016.72 0l3.84 11.328L0 9.336l5.04 3.336-2.208 2.664h18.336l-2.208-2.664L24 9.336l-4.56 1.992L23.28 0z"/>
  </svg>
);

const GoDaddyIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path fill="#1BDBDB" d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 17.08c-.32.426-.747.64-1.28.64-.427 0-.8-.107-1.12-.32-.32-.213-.587-.533-.8-.96l-1.494-2.987-1.493 2.987c-.213.427-.48.747-.8.96-.32.213-.693.32-1.12.32-.533 0-.96-.214-1.28-.64-.32-.427-.48-.96-.48-1.6V9.52c0-.64.16-1.173.48-1.6.32-.427.747-.64 1.28-.64s.96.213 1.28.64c.32.427.48.96.48 1.6v3.307l1.653-3.307c.213-.427.48-.747.8-.96.32-.213.693-.32 1.12-.32.427 0 .8.107 1.12.32.32.213.587.533.8.96l1.654 3.307V9.52c0-.64.16-1.173.48-1.6.32-.427.747-.64 1.28-.64s.96.213 1.28.64c.32.427.48.96.48 1.6v5.96c0 .64-.16 1.173-.48 1.6z"/>
  </svg>
);

const MicrosoftIcon = () => (
  <svg viewBox="0 0 23 23" className="w-6 h-6">
    <path fill="#f25022" d="M1 1h10v10H1z"/>
    <path fill="#00a4ef" d="M1 12h10v10H1z"/>
    <path fill="#7fba00" d="M12 1h10v10H12z"/>
    <path fill="#ffb900" d="M12 12h10v10H12z"/>
  </svg>
);

// FAQ data
const faqData = [
  {
    question: "What is an App Password?",
    answer: "An App Password is a 16-character code that gives an app permission to access your email account. It's required when you have two-factor authentication (2FA) enabled, which blocks regular password logins from third-party apps for security reasons."
  },
  {
    question: "Is my password safe?",
    answer: "Yes! Your app passwords are encrypted using industry-standard AES-256 encryption before being stored. We never have access to your actual email password, and app passwords can be revoked at any time from your email provider's settings."
  },
  {
    question: "Why do I need to create an App Password?",
    answer: "Most email providers (Gmail, Outlook, etc.) block third-party apps from using your regular password for security. App Passwords let you grant specific access to our app without sharing your main password or disabling 2FA."
  },
  {
    question: "Can I use OAuth instead?",
    answer: "Yes! For Microsoft 365 and Google Workspace accounts, you can use the 'Connect with Microsoft' or 'Connect with Google' buttons for a simpler OAuth-based connection that doesn't require app passwords."
  },
  {
    question: "What permissions does the app need?",
    answer: "We only request read access to your contacts and inbox headers (From/To fields). We never read email content, send emails on your behalf, or access any other data."
  },
  {
    question: "How do I disconnect my account?",
    answer: "Go to the main dashboard, find your connected account, and click the trash icon to disconnect. You can also revoke the app password from your email provider's security settings."
  },
  {
    question: "What's the difference between Business and Personal contacts?",
    answer: "We automatically classify contacts based on their email domain. Personal emails (gmail.com, yahoo.com, etc.) are marked as 'Personal', while company domains are marked as 'Business'. You can export filtered lists of either type."
  },
  {
    question: "Can I extract contacts from multiple accounts?",
    answer: "Yes! You can connect as many email accounts as you need. Each account's contacts are extracted separately, and you can export all contacts together or filter by account."
  }
];

// Provider setup guides
const providerGuides = {
  gmail: {
    name: "Gmail / Google",
    icon: GmailIcon,
    color: "from-red-500 to-orange-500",
    requirements: [
      "Google Account with 2-Step Verification enabled",
      "Access to Google Account security settings"
    ],
    steps: [
      {
        title: "Enable 2-Step Verification (if not already enabled)",
        details: [
          "Go to myaccount.google.com",
          "Click 'Security' in the left menu",
          "Under 'Signing in to Google', click '2-Step Verification'",
          "Follow the prompts to set up 2FA"
        ]
      },
      {
        title: "Create an App Password",
        details: [
          "Go to myaccount.google.com/apppasswords",
          "Sign in if prompted",
          "Under 'Select app', choose 'Mail'",
          "Under 'Select device', choose 'Other' and type 'Email Extractor'",
          "Click 'Generate'"
        ]
      },
      {
        title: "Copy Your App Password",
        details: [
          "A 16-character password will appear (like: xxxx xxxx xxxx xxxx)",
          "Copy this password - you won't be able to see it again!",
          "Use this password when connecting your account"
        ]
      }
    ],
    link: "https://myaccount.google.com/apppasswords",
    notes: "App Passwords are only available when 2-Step Verification is enabled."
  },
  outlook: {
    name: "Outlook / Hotmail",
    icon: OutlookIcon,
    color: "from-blue-500 to-cyan-500",
    requirements: [
      "Microsoft Account",
      "Two-step verification enabled"
    ],
    steps: [
      {
        title: "Enable Two-Step Verification",
        details: [
          "Go to account.microsoft.com/security",
          "Sign in to your Microsoft account",
          "Click 'Advanced security options'",
          "Turn on 'Two-step verification' if not enabled"
        ]
      },
      {
        title: "Create an App Password",
        details: [
          "On the security page, find 'App passwords'",
          "Click 'Create a new app password'",
          "A password will be generated automatically"
        ]
      },
      {
        title: "Use the App Password",
        details: [
          "Copy the generated password",
          "Use this instead of your regular password",
          "The password is shown only once - save it securely"
        ]
      }
    ],
    link: "https://account.microsoft.com/security",
    notes: "For Microsoft 365 work accounts, use the 'Connect with Microsoft' OAuth option instead."
  },
  microsoft365: {
    name: "Microsoft 365 (Work/School)",
    icon: MicrosoftIcon,
    color: "from-blue-600 to-indigo-600",
    requirements: [
      "Microsoft 365 work or school account",
      "Organization must allow third-party app access"
    ],
    steps: [
      {
        title: "Use OAuth Connection (Recommended)",
        details: [
          "Click 'Connect with Microsoft' button on the dashboard",
          "Sign in with your work/school Microsoft account",
          "Review and accept the permissions requested",
          "You'll be redirected back automatically"
        ]
      },
      {
        title: "Alternative: IMAP with App Password",
        details: [
          "If OAuth doesn't work, your admin may have disabled it",
          "Ask your IT admin to enable 'IMAP' in Exchange settings",
          "Create an app password at account.microsoft.com/security",
          "Use simple email + password login"
        ]
      }
    ],
    link: "https://admin.microsoft.com",
    notes: "OAuth is the recommended method for Microsoft 365 accounts. Contact your IT admin if you can't connect."
  },
  icloud: {
    name: "iCloud Mail",
    icon: ICloudIcon,
    color: "from-blue-400 to-sky-500",
    requirements: [
      "Apple ID with two-factor authentication",
      "Access to appleid.apple.com"
    ],
    steps: [
      {
        title: "Sign in to Apple ID",
        details: [
          "Go to appleid.apple.com",
          "Sign in with your Apple ID",
          "Complete two-factor authentication if prompted"
        ]
      },
      {
        title: "Generate App-Specific Password",
        details: [
          "In the Sign-In and Security section, click 'App-Specific Passwords'",
          "Click the '+' button to create a new password",
          "Enter a label like 'Email Extractor'",
          "Click 'Create'"
        ]
      },
      {
        title: "Copy and Use Password",
        details: [
          "Apple will show a password in format: xxxx-xxxx-xxxx-xxxx",
          "Copy this password immediately",
          "Use this when connecting your iCloud account"
        ]
      }
    ],
    link: "https://appleid.apple.com/account/manage",
    notes: "App-specific passwords are required for all third-party apps accessing iCloud Mail."
  },
  yahoo: {
    name: "Yahoo Mail",
    icon: YahooIcon,
    color: "from-purple-600 to-violet-600",
    requirements: [
      "Yahoo account",
      "Account Key or two-step verification enabled"
    ],
    steps: [
      {
        title: "Access Yahoo Security Settings",
        details: [
          "Go to login.yahoo.com",
          "Click on your profile icon → Account Info",
          "Click 'Account Security'"
        ]
      },
      {
        title: "Generate App Password",
        details: [
          "Scroll down to 'Generate app password'",
          "Select 'Other App' from the dropdown",
          "Enter 'Email Extractor' as the app name",
          "Click 'Generate'"
        ]
      },
      {
        title: "Copy Your Password",
        details: [
          "Yahoo will display a unique password",
          "Copy this password - it won't be shown again",
          "Use this password to connect your account"
        ]
      }
    ],
    link: "https://login.yahoo.com/account/security",
    notes: "Yahoo requires 2-step verification or Account Key to generate app passwords."
  },
  godaddy: {
    name: "GoDaddy Workspace",
    icon: GoDaddyIcon,
    color: "from-teal-500 to-emerald-500",
    requirements: [
      "GoDaddy Workspace Email account",
      "Admin access or standard email credentials"
    ],
    steps: [
      {
        title: "Find Your Email Settings",
        details: [
          "Log in to your GoDaddy account",
          "Go to Email & Office → Manage",
          "Click on your email address"
        ]
      },
      {
        title: "Get IMAP Settings",
        details: [
          "Server: imap.secureserver.net",
          "Port: 993",
          "Security: SSL/TLS",
          "Username: Your full email address"
        ]
      },
      {
        title: "Use Your Email Password",
        details: [
          "GoDaddy uses your regular email password",
          "No app-specific password is required",
          "Simply enter your email and password to connect"
        ]
      }
    ],
    link: "https://email.godaddy.com",
    notes: "GoDaddy Workspace Email uses your standard email password for IMAP access."
  }
};

export default function HelpPage({ onBack }) {
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const filteredFaq = faqData.filter(item => 
    item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#09090b] text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-zinc-900/50 sticky top-0 z-10 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={onBack}
            className="hover:bg-white/5"
            data-testid="help-back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold">Help Center</h1>
            <p className="text-sm text-zinc-400">Setup guides and FAQ</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-12">
        
        {/* Quick Overview */}
        <section>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-indigo-500/10 to-violet-500/10 border-indigo-500/20">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="p-3 rounded-xl bg-indigo-500/20">
                  <Mail className="w-6 h-6 text-indigo-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Connect</h3>
                  <p className="text-sm text-zinc-400">Link your email accounts securely</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/20">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="p-3 rounded-xl bg-emerald-500/20">
                  <Shield className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Extract</h3>
                  <p className="text-sm text-zinc-400">Pull contacts from inbox & contacts</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="p-3 rounded-xl bg-amber-500/20">
                  <Key className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Export</h3>
                  <p className="text-sm text-zinc-400">Download CSV for your campaigns</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Email Provider Setup Guides */}
        <section>
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
            <Mail className="w-6 h-6 text-indigo-400" />
            Email Provider Setup Guides
          </h2>
          
          <div className="space-y-4">
            {Object.entries(providerGuides).map(([key, provider]) => (
              <Card 
                key={key}
                className={`bg-zinc-900/50 border-white/10 overflow-hidden transition-all ${
                  expandedProvider === key ? 'ring-1 ring-indigo-500/50' : ''
                }`}
              >
                <button
                  className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                  onClick={() => setExpandedProvider(expandedProvider === key ? null : key)}
                  data-testid={`provider-${key}-toggle`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${provider.color}`}>
                      <provider.icon />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-white">{provider.name}</h3>
                      <p className="text-sm text-zinc-400">Click to view setup guide</p>
                    </div>
                  </div>
                  {expandedProvider === key ? (
                    <ChevronUp className="w-5 h-5 text-zinc-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-zinc-400" />
                  )}
                </button>
                
                {expandedProvider === key && (
                  <div className="px-4 pb-4 border-t border-white/5 pt-4">
                    {/* Requirements */}
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-zinc-400 mb-2">Requirements</h4>
                      <ul className="space-y-1">
                        {provider.requirements.map((req, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Steps */}
                    <div className="space-y-4">
                      {provider.steps.map((step, i) => (
                        <div key={i} className="relative pl-8">
                          <div className="absolute left-0 top-0 w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center text-sm font-medium text-indigo-400">
                            {i + 1}
                          </div>
                          <h4 className="font-medium text-white mb-2">{step.title}</h4>
                          <ul className="space-y-1">
                            {step.details.map((detail, j) => (
                              <li key={j} className="text-sm text-zinc-400 flex items-start gap-2">
                                <span className="text-zinc-600 mt-1">•</span>
                                {detail}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>

                    {/* Notes */}
                    {provider.notes && (
                      <div className="mt-6 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-200">{provider.notes}</p>
                      </div>
                    )}

                    {/* Link */}
                    <div className="mt-4 flex items-center gap-4">
                      <a
                        href={provider.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Open {provider.name} Settings
                      </a>
                      <button
                        onClick={() => copyToClipboard(provider.link)}
                        className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white"
                      >
                        <Copy className="w-4 h-4" />
                        Copy Link
                      </button>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section>
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
            <HelpCircle className="w-6 h-6 text-indigo-400" />
            Frequently Asked Questions
          </h2>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              placeholder="Search FAQ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-zinc-900/50 border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
              data-testid="faq-search"
            />
          </div>

          <div className="space-y-3">
            {filteredFaq.map((item, i) => (
              <Card 
                key={i}
                className="bg-zinc-900/50 border-white/10 overflow-hidden"
              >
                <button
                  className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors text-left"
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  data-testid={`faq-${i}-toggle`}
                >
                  <span className="font-medium text-white pr-4">{item.question}</span>
                  {expandedFaq === i ? (
                    <ChevronUp className="w-5 h-5 text-zinc-400 shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-zinc-400 shrink-0" />
                  )}
                </button>
                
                {expandedFaq === i && (
                  <div className="px-4 pb-4 border-t border-white/5 pt-3">
                    <p className="text-sm text-zinc-400 leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </Card>
            ))}
            
            {filteredFaq.length === 0 && (
              <div className="text-center py-8 text-zinc-500">
                No results found for "{searchQuery}"
              </div>
            )}
          </div>
        </section>

        {/* Contact Support */}
        <section>
          <Card className="bg-gradient-to-br from-indigo-500/10 to-violet-500/10 border-indigo-500/20">
            <CardContent className="p-8 text-center">
              <h3 className="text-xl font-semibold mb-2">Still need help?</h3>
              <p className="text-zinc-400 mb-6">
                Contact us at <a href="mailto:support@pro-email.app" className="text-indigo-400 hover:underline">support@pro-email.app</a>
              </p>
              <Button 
                className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500"
                onClick={() => window.location.href = 'mailto:support@pro-email.app'}
              >
                <Mail className="w-4 h-4 mr-2" />
                Contact Support
              </Button>
            </CardContent>
          </Card>
        </section>

      </div>
    </div>
  );
}
